import { useEffect, useMemo, useRef, useState } from "react";

import { useLocation, useNavigate } from "react-router-dom";

import { AlertCircle, Clock8, UserCircle } from "lucide-react";

import { drawQrOnCanvas, generateQrMatrix } from "../Utils/qrEncoder";

// import qrLogo from "../../assets/Title16x16.svg";
// import brandLogo from "../../assets/images/Anlook_big_logo1.svg";
// import {
//   checkCreditApi,
//   payWithCreditApi,
//   getCartDetails,
// } from "../../Services/CatelogueApi";

const parsePaymentResponse = (paymentResponse) => {
  if (!paymentResponse) {
    return null;
  }

  if (typeof paymentResponse === "string") {
    try {
      return JSON.parse(paymentResponse);
    } catch (error) {
      console.error("Invalid payment response:", error);

      return null;
    }
  }

  return paymentResponse;
};

const getPaymentDetails = (paymentLink) => {
  if (!paymentLink) {
    return {
      amount: "",
      payee: "",
      transactionId: "",
    };
  }

  try {
    const queryString = paymentLink.split("?")[1] || "";

    const parameters = new URLSearchParams(queryString);

    return {
      amount: parameters.get("am") || parameters.get("mam") || "",

      payee: parameters.get("pn") || "Merchant",

      transactionId: parameters.get("tr") || parameters.get("tid") || "",
    };
  } catch (error) {
    console.error("Unable to parse payment link:", error);

    return {
      amount: "",
      payee: "",
      transactionId: "",
    };
  }
};

const drawLogoOnQrCanvas = ({ canvas, logoSource, logoScale = 0.09 }) =>
  new Promise((resolve, reject) => {
    if (!canvas || !logoSource) {
      resolve();
      return;
    }

    const context = canvas.getContext("2d");

    if (!context) {
      reject(new Error("Canvas is not supported."));
      return;
    }

    const image = new Image();

    image.onload = () => {
      /*
       * canvas.width and canvas.height are backing-store dimensions.
       * On mobile, they can be 2x or 3x larger because of devicePixelRatio.
       *
       * The QR context is already scaled to CSS pixels, so the logo must
       * also use CSS-pixel dimensions.
       */
      const pixelRatio = Math.max(1, Math.min(window.devicePixelRatio || 1, 3));

      const logicalCanvasWidth = canvas.width / pixelRatio;

      const logicalCanvasHeight = canvas.height / pixelRatio;

      const logoSize = Math.max(
        18,
        Math.floor(
          Math.min(logicalCanvasWidth, logicalCanvasHeight) * logoScale,
        ),
      );

      const whitePadding = Math.max(3, Math.floor(logoSize * 0.14));

      const backgroundSize = logoSize + whitePadding * 2;

      const backgroundX = Math.floor((logicalCanvasWidth - backgroundSize) / 2);

      const backgroundY = Math.floor(
        (logicalCanvasHeight - backgroundSize) / 2,
      );

      const logoX = Math.floor((logicalCanvasWidth - logoSize) / 2);

      const logoY = Math.floor((logicalCanvasHeight - logoSize) / 2);

      context.save();

      context.fillStyle = "#ffffff";

      context.fillRect(
        backgroundX,
        backgroundY,
        backgroundSize,
        backgroundSize,
      );

      context.drawImage(image, logoX, logoY, logoSize, logoSize);

      context.restore();

      resolve();
    };

    image.onerror = () => {
      reject(new Error("Unable to load the QR logo."));
    };

    image.src = logoSource;
  });
const createUpiAppLinks = (paymentLink) => {
  if (!paymentLink) {
    return {
      googlePay: "",
      phonePe: "",
      paytm: "",
      generic: "",
    };
  }

  try {
    const questionMarkIndex = paymentLink.indexOf("?");

    if (questionMarkIndex === -1) {
      return {
        googlePay: paymentLink,
        phonePe: paymentLink,
        paytm: paymentLink,
        generic: paymentLink,
      };
    }

    const queryString = paymentLink.slice(questionMarkIndex + 1);

    const isAndroid =
      typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent);

    /*
     * Android intent URLs target the selected installed package.
     * On other devices, app-specific URL schemes are used.
     */
    if (isAndroid) {
      return {
        googlePay:
          `intent://pay?${queryString}` +
          "#Intent;" +
          "scheme=upi;" +
          "package=com.google.android.apps.nbu.paisa.user;" +
          "end",

        phonePe:
          `intent://pay?${queryString}` +
          "#Intent;" +
          "scheme=upi;" +
          "package=com.phonepe.app;" +
          "end",

        paytm:
          `intent://pay?${queryString}` +
          "#Intent;" +
          "scheme=upi;" +
          "package=net.one97.paytm;" +
          "end",

        generic: paymentLink,
      };
    }

    return {
      googlePay: `gpay://upi/pay?${queryString}`,
      phonePe: `phonepe://pay?${queryString}`,
      paytm: `paytmmp://pay?${queryString}`,
      generic: paymentLink,
    };
  } catch (error) {
    console.error("Unable to create UPI app links:", error);

    return {
      googlePay: paymentLink,
      phonePe: paymentLink,
      paytm: paymentLink,
      generic: paymentLink,
    };
  }
};
const parseSocketMessage = (eventData) => {
  if (!eventData) {
    return null;
  }

  if (typeof eventData === "object") {
    return eventData;
  }

  try {
    return JSON.parse(eventData);
  } catch {
    return {
      Message: String(eventData),
    };
  }
};

const isPaymentReceivedMessage = ({ socketData, orderId }) => {
  const senderId = String(
    socketData?.SenderId ??
      socketData?.Senderld ??
      socketData?.senderId ??
      socketData?.senderld ??
      "",
  )
    .trim()
    .toLowerCase();

  const message = String(
    socketData?.Message ?? socketData?.message ?? "",
  ).trim();

  const normalizedMessage = message.toLowerCase();

  const expectedOrderText = `order #${orderId}`.toLowerCase();

  return (
    senderId === "system" &&
    normalizedMessage.includes("payment received") &&
    normalizedMessage.includes(expectedOrderText)
  );
};

const getCataloguePaymentKey = (orderId) =>
  `catalogue_payment:${String(orderId || "").trim()}`;
const PaymentQr = ({
  paymentResponse: paymentResponseProp,

  // DIRECT PAYMENT DATA FROM CHAAFO CHECKOUT
  paymentLink: paymentLinkProp = "",
  orderId: orderIdProp = "",
  userid: useridProp = "",
  custMobile: custMobileProp = "",
  orderType: orderTypeProp = "",
  amount: amountProp = "",
  clientName: clientNameProp = "",
  flow: flowProp = "",

  // WHEN USED AS MODAL
  embedded = false,
  onPaymentSuccess,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const storedPaymentData = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search);

      const currentOrderId = String(
        location.state?.orderId || params.get("orderid") || "",
      ).trim();

      const requestedFlow = String(location.state?.flow || "")
        .trim()
        .toLowerCase();

      /*
       * BULK FLOW:
       * keep exactly the existing
       * sessionStorage behavior.
       */
      if (requestedFlow === "bulk") {
        return JSON.parse(
          sessionStorage.getItem("bulk_food_payment_data") || "{}",
        );
      }

      /*
       * CATALOGUE:
       * first try permanent order-based storage.
       */
      if (currentOrderId) {
        const permanentData = JSON.parse(
          localStorage.getItem(getCataloguePaymentKey(currentOrderId)) ||
            "null",
        );

        if (permanentData?.flow === "catalogue") {
          return permanentData;
        }
      }

      /*
       * Old catalogue compatibility.
       */
      const catalogueSessionData = JSON.parse(
        sessionStorage.getItem("catalogue_payment_data") || "null",
      );

      if (catalogueSessionData) {
        return catalogueSessionData;
      }

      /*
       * Finally bulk fallback.
       */
      return JSON.parse(
        sessionStorage.getItem("bulk_food_payment_data") || "{}",
      );
    } catch (error) {
      console.error("Unable to restore payment:", error);

      return {};
    }
  }, [location.state]);

  const paymentResponse =
    paymentResponseProp ||
    location.state?.paymentResponse ||
    storedPaymentData?.paymentResponse ||
    null;

  const paymentFlow = String(
    flowProp || location.state?.flow || storedPaymentData?.flow || "bulk",
  )
    .trim()
    .toLowerCase();

  const isBulkPayment = paymentFlow === "bulk";

  const isCataloguePayment = paymentFlow === "catalogue";

  const orderId = String(
    orderIdProp || location.state?.orderId || storedPaymentData?.orderId || "",
  ).trim();
  const userid = String(
    useridProp ||
      location.state?.userid ||
      storedPaymentData?.userid ||
      new URLSearchParams(window.location.search).get("userid") ||
      "",
  ).trim();

  const custMobile = String(
    custMobileProp ||
      location.state?.custMobile ||
      storedPaymentData?.custMobile ||
      new URLSearchParams(window.location.search).get("mobile") ||
      "",
  ).trim();

  const orderType = String(
    orderTypeProp ||
      location.state?.orderType ||
      storedPaymentData?.orderType ||
      new URLSearchParams(window.location.search).get("orderType") ||
      "",
  ).trim();

  const restaurantNumber = String(
    location.state?.restaurantNumber ||
      storedPaymentData?.restaurantNumber ||
      new URLSearchParams(window.location.search).get("restaurantNumber") ||
      "",
  ).trim();

  const clientName =
    clientNameProp ||
    location.state?.clientName ||
    storedPaymentData?.clientName ||
    "";

  const navigateToReceipt = () => {
    if (!orderId || paymentCompletedRef.current) {
      return;
    }

    paymentCompletedRef.current = true;

    // ================================================
    // MODAL MODE → CHECKOUT WILL HANDLE SUCCESS
    // ================================================

    if (typeof onPaymentSuccess === "function") {
      onPaymentSuccess(orderId);
      return;
    }

    // ================================================
    // OLD PAGE FLOW
    // ================================================

    const receiptParams = new URLSearchParams();

    receiptParams.set("orderid", orderId);

    if (isBulkPayment) {
      receiptParams.set("bulk", "1");
    }

    if (restaurantNumber) {
      receiptParams.set("restaurantNumber", restaurantNumber);
    }

    if (isCataloguePayment) {
      sessionStorage.removeItem("catalogue_payment_data");
    }

    navigate(
      {
        pathname: "/receipt",
        search: `?${receiptParams.toString()}`,
      },
      {
        replace: true,
      },
    );
  };
  useEffect(() => {
    // Modal ke andar use ho raha hai,
    // browser history ko touch mat karo.
    if (embedded) {
      return undefined;
    }

    window.history.replaceState(
      {
        ...(window.history.state || {}),
        paymentLocked: true,
      },
      "",
      window.location.href,
    );

    const preventBackToCart = () => {
      window.history.pushState(
        {
          paymentLocked: true,
        },
        "",
        window.location.href,
      );
    };

    window.history.pushState(
      {
        paymentLocked: true,
      },
      "",
      window.location.href,
    );

    window.addEventListener("popstate", preventBackToCart);

    return () => {
      window.removeEventListener("popstate", preventBackToCart);
    };
  }, [embedded]);

  const canvasRef = useRef(null);
  const qrContainerRef = useRef(null);
  const paymentCompletedRef = useRef(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const [creditAllowed, setCreditAllowed] = useState(false);

  const [checkingCredit, setCheckingCredit] = useState(false);

  const [payingWithCredit, setPayingWithCredit] = useState(false);

  const [creditError, setCreditError] = useState("");

  const [checkingOrderValidity, setCheckingOrderValidity] =
    useState(isCataloguePayment);

  const [orderLinkExpired, setOrderLinkExpired] = useState(false);

  const [qrGenerated, setQrGenerated] = useState(false);

  const [qrGenerating, setQrGenerating] = useState(false);

  const [qrError, setQrError] = useState("");

  const parsedResponse = useMemo(
    () => parsePaymentResponse(paymentResponse),
    [paymentResponse],
  );

  const paymentLink = String(
    paymentLinkProp ||
      (parsedResponse?.responseCode === "00"
        ? parsedResponse?.response?.paymentLink
        : "") ||
      "",
  ).trim();

  const paymentDetails = useMemo(() => {
    const details = getPaymentDetails(paymentLink);

    return {
      ...details,

      amount: String(amountProp || "").trim() || details.amount,
    };
  }, [paymentLink, amountProp]);
  const upiAppLinks = useMemo(
    () => createUpiAppLinks(paymentLink),
    [paymentLink],
  );
  useEffect(() => {
    setQrGenerated(false);
    setQrGenerating(false);
    setQrError("");
  }, [paymentLink]);

  useEffect(() => {
    if (!embedded || !paymentLink) {
      return undefined;
    }

    console.log("🔗 QR PAYMENT LINK:", paymentLink);

    setQrError("");
    setQrGenerating(true);
    setQrGenerated(false);

    const timer = window.setTimeout(() => {
      setQrGenerating(false);
      setQrGenerated(true);
    }, 300);

    return () => {
      window.clearTimeout(timer);
    };
  }, [embedded, paymentLink]);

  useEffect(() => {
    if (
      !qrGenerated ||
      !paymentLink ||
      !canvasRef.current ||
      !qrContainerRef.current
    ) {
      return undefined;
    }

    let animationFrameId = null;
    let isCancelled = false;

    const drawPaymentQr = async () => {
      if (isCancelled || !canvasRef.current || !qrContainerRef.current) {
        return;
      }

      try {
        const matrix = generateQrMatrix(paymentLink);

        drawQrOnCanvas(canvasRef.current, matrix, {
          maximumSize: 420,
          quietZoneModules: 4,
          foreground: "#000000",
          background: "#ffffff",
        });

        if (!isCancelled) {
          setQrError("");
        }
      } catch (error) {
        if (isCancelled) {
          return;
        }

        console.error("QR generation failed:", error);

        setQrGenerated(false);

        setQrError(error?.message || "Unable to generate the payment QR.");
      }
    };

    const scheduleQrDraw = () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = window.requestAnimationFrame(() => {
        drawPaymentQr();
      });
    };

    scheduleQrDraw();

    let resizeObserver = null;

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(scheduleQrDraw);

      resizeObserver.observe(qrContainerRef.current);
    } else {
      window.addEventListener("resize", scheduleQrDraw);
    }

    return () => {
      isCancelled = true;

      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }

      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener("resize", scheduleQrDraw);
      }
    };
  }, [qrGenerated, paymentLink]);
  useEffect(() => {
    if (
      !orderId ||
      (isCataloguePayment && (checkingOrderValidity || orderLinkExpired))
    ) {
      return undefined;
    }

    let socket = null;
    let reconnectTimer = null;
    let manuallyClosed = false;
    let reconnectAttempts = 0;

    const connectSocket = () => {
      if (manuallyClosed) {
        return;
      }

      const encodedOrderId = encodeURIComponent(orderId);

      const roomId = encodeURIComponent(`${orderId}_order_update_master_v2`);

      const socketUrl =
        `wss://api.anlook.com/ws` +
        `?userId=${encodedOrderId}` +
        `&roomId=${roomId}`;

      // Prevent duplicate connections
      if (
        socket &&
        (socket.readyState === WebSocket.OPEN ||
          socket.readyState === WebSocket.CONNECTING)
      ) {
        return;
      }

      socket = new WebSocket(socketUrl);

      socket.onopen = () => {
        console.log("Payment WebSocket connected");

        setSocketConnected(true);

        // Reset reconnect count after successful connection
        reconnectAttempts = 0;

        if (reconnectTimer) {
          window.clearTimeout(reconnectTimer);

          reconnectTimer = null;
        }
      };

      socket.onmessage = (event) => {
        const socketData = parseSocketMessage(event.data);

        if (
          !isPaymentReceivedMessage({
            socketData,
            orderId,
          })
        ) {
          return;
        }

        const message = String(
          socketData?.Message ??
            socketData?.message ??
            `Payment received for order #${orderId}`,
        );

        if (isBulkPayment) {
          /*
           * BULK:
           * keep existing behaviour.
           */
          const paymentReceivedKey = `bulk_food_payment_received:${orderId}`;

          sessionStorage.setItem(
            paymentReceivedKey,
            JSON.stringify({
              orderId,
              received: true,
              message,
              receivedAt: new Date().toISOString(),
            }),
          );
        } else if (isCataloguePayment) {
          /*
           * CATALOGUE:
           * persist payment completion
           * against this order ID.
           */
          const paymentKey = getCataloguePaymentKey(orderId);

          let existingPayment = {};

          try {
            existingPayment = JSON.parse(
              localStorage.getItem(paymentKey) || "{}",
            );
          } catch {
            existingPayment = {};
          }

          localStorage.setItem(
            paymentKey,
            JSON.stringify({
              ...existingPayment,

              status: "payment_received",

              flow: "catalogue",

              orderId,

              received: true,

              message,

              receivedAt: new Date().toISOString(),
            }),
          );
        }

        // Payment received:
        // do NOT reconnect anymore
        manuallyClosed = true;

        if (reconnectTimer) {
          window.clearTimeout(reconnectTimer);

          reconnectTimer = null;
        }

        if (
          socket &&
          (socket.readyState === WebSocket.OPEN ||
            socket.readyState === WebSocket.CONNECTING)
        ) {
          socket.close(1000, "Payment received");
        }

        navigateToReceipt();
      };

      socket.onerror = (error) => {
        console.error("Payment WebSocket error:", error);

        /*
         * Don't call connectSocket() directly here.
         * onclose will handle reconnection.
         *
         * Some browsers fire both error + close.
         * Reconnecting from both can create duplicate sockets.
         */
      };

      socket.onclose = (event) => {
        console.log(
          "Payment WebSocket disconnected:",
          event.code,
          event.reason,
        );

        setSocketConnected(false);

        if (manuallyClosed) {
          return;
        }

        reconnectAttempts += 1;

        /*
         * Reconnect almost immediately.
         *
         * Attempt 1: 250ms
         * Attempt 2: 500ms
         * Attempt 3+: 1000ms
         *
         * This avoids creating a reconnect loop
         * hundreds of times per second if server
         * itself is unavailable.
         */
        const reconnectDelay =
          reconnectAttempts === 1 ? 250 : reconnectAttempts === 2 ? 500 : 1000;

        if (reconnectTimer) {
          window.clearTimeout(reconnectTimer);
        }

        reconnectTimer = window.setTimeout(() => {
          reconnectTimer = null;

          if (!manuallyClosed) {
            console.log("Reconnecting payment WebSocket...");

            connectSocket();
          }
        }, reconnectDelay);
      };
    };

    connectSocket();

    return () => {
      manuallyClosed = true;

      if (reconnectTimer) {
        window.clearTimeout(reconnectTimer);

        reconnectTimer = null;
      }

      if (
        socket &&
        (socket.readyState === WebSocket.OPEN ||
          socket.readyState === WebSocket.CONNECTING)
      ) {
        socket.close(1000, "Payment screen closed");
      }
    };
  }, [
    orderId,
    restaurantNumber,
    navigate,
    isBulkPayment,
    isCataloguePayment,
    checkingOrderValidity,
    orderLinkExpired,
  ]);

  useEffect(() => {
    if (!orderId) {
      return;
    }

    try {
      /*
       * Bulk keeps its existing
       * session based status.
       */
      if (isBulkPayment) {
        if (!isCataloguePayment) {
          return;
        }

        const savedStatus = JSON.parse(
          sessionStorage.getItem(`bulk_food_payment_received:${orderId}`) ||
            "null",
        );

        if (savedStatus?.received) {
          navigateToReceipt();
        }

        return;
      }

      /*
       * Catalogue uses persistent
       * local storage.
       */
      const savedPayment = JSON.parse(
        localStorage.getItem(getCataloguePaymentKey(orderId)) || "null",
      );

      if (
        savedPayment?.status === "payment_received" ||
        savedPayment?.status === "completed"
      ) {
        navigateToReceipt();
      }
    } catch (error) {
      console.error("Unable to check saved payment status:", error);
    }
  }, [orderId, restaurantNumber, navigate, isBulkPayment]);
  useEffect(() => {
    if (
      !isCataloguePayment ||
      !orderId ||
      checkingOrderValidity ||
      orderLinkExpired
    ) {
      return;
    }

    let cancelled = false;

    const checkCredit = async () => {
      setCheckingCredit(true);
      setCreditError("");

      try {
        const result = await checkCreditApi({
          orderId,
        });

        if (cancelled) {
          return;
        }

        setCreditAllowed(result?.responseCode === "00");
      } catch (error) {
        if (cancelled) {
          return;
        }

        console.error("Check credit error:", error);

        setCreditAllowed(false);
      } finally {
        if (!cancelled) {
          setCheckingCredit(false);
        }
      }
    };

    checkCredit();

    return () => {
      cancelled = true;
    };
  }, [isCataloguePayment, orderId, checkingOrderValidity, orderLinkExpired]);

  useEffect(() => {
    // Don't touch bulk
    if (!isCataloguePayment) {
      setCheckingOrderValidity(false);

      return;
    }

    if (!orderId) {
      setCheckingOrderValidity(false);

      return;
    }

    let cancelled = false;

    const checkOrderValidity = async () => {
      setCheckingOrderValidity(true);

      try {
        const result = await getCartDetails({
          orderId,
          orderType,
        });

        if (cancelled) {
          return;
        }

        if (result?.responseCode === "03") {
          localStorage.removeItem(getCataloguePaymentKey(orderId));

          sessionStorage.removeItem("catalogue_payment_data");

          setOrderLinkExpired(true);

          return;
        }

        setOrderLinkExpired(false);
      } catch (error) {
        console.error("Unable to check order validity:", error);
      } finally {
        if (!cancelled) {
          setCheckingOrderValidity(false);
        }
      }
    };

    checkOrderValidity();

    return () => {
      cancelled = true;
    };
  }, [isCataloguePayment, orderId, orderType]);

  const handleGenerateQr = () => {
    if (
      checkingOrderValidity ||
      orderLinkExpired ||
      !paymentLink ||
      qrGenerating
    ) {
      return;
    }

    setQrError("");
    setQrGenerating(true);

    window.setTimeout(() => {
      setQrGenerated(true);
      setQrGenerating(false);
    }, 400);
  };

  const handleRegenerateQr = () => {
    if (
      checkingOrderValidity ||
      orderLinkExpired ||
      !paymentLink ||
      qrGenerating
    ) {
      return;
    }
    setQrError("");
    setQrGenerated(false);
    setQrGenerating(true);

    window.setTimeout(() => {
      setQrGenerated(true);
      setQrGenerating(false);
    }, 350);
  };

  const handleOpenSpecificUpiApp = (appLink) => {
    if (checkingOrderValidity || orderLinkExpired) {
      return;
    }

    if (!appLink) {
      return;
    }

    window.location.href = appLink;
  };
  const handlePayWithCredit = async () => {
    if (
      !isCataloguePayment ||
      !creditAllowed ||
      payingWithCredit ||
      !orderId ||
      !userid ||
      !custMobile
    ) {
      return;
    }

    setPayingWithCredit(true);
    setCreditError("");

    try {
      const result = await payWithCreditApi({
        custMobile,
        orderId,
        userid,
      });

      if (result?.responseCode !== "00") {
        throw new Error(
          result?.responseMessage || "Unable to pay with credit.",
        );
      }

      /*
       * Mark catalogue order as paid
       * in permanent local cache.
       *
       * This ensures reopening /food
       * goes to receipt instead of QR.
       */
      const paymentKey = getCataloguePaymentKey(orderId);

      let existingPayment = {};

      try {
        existingPayment = JSON.parse(localStorage.getItem(paymentKey) || "{}");
      } catch {
        existingPayment = {};
      }

      localStorage.setItem(
        paymentKey,
        JSON.stringify({
          ...existingPayment,

          status: "payment_received",

          flow: "catalogue",

          orderId,

          paymentMethod: "credit",

          received: true,

          message: result?.responseMessage || "Payment completed with credit.",

          receivedAt: new Date().toISOString(),
        }),
      );

      /*
       * Same destination as normal
       * successful UPI payment.
       */
      navigateToReceipt();
    } catch (error) {
      console.error("Pay with credit error:", error);

      setCreditError(
        error?.response?.data?.responseMessage ||
          error?.message ||
          "Unable to complete credit payment.",
      );
    } finally {
      setPayingWithCredit(false);
    }
  };
  const handleOpenPaymentApp = () => {
    if (checkingOrderValidity || orderLinkExpired) {
      return;
    }

    if (!upiAppLinks.generic) {
      return;
    }

    window.location.href = upiAppLinks.generic;
  };

  const handleDownloadQr = () => {
    const canvas = canvasRef.current;

    if (!canvas || !qrGenerated) {
      return;
    }

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setQrError("Unable to prepare the QR image.");
          return;
        }

        const objectUrl = URL.createObjectURL(blob);
        const downloadLink = document.createElement("a");

        const safeTransactionId = String(
          paymentDetails.transactionId || "payment",
        )
          .replace(/[^a-zA-Z0-9_-]/g, "-")
          .slice(0, 60);

        downloadLink.href = objectUrl;
        downloadLink.download = `anlook-upi-qr-${safeTransactionId}.png`;

        document.body.appendChild(downloadLink);
        downloadLink.click();
        downloadLink.remove();

        window.setTimeout(() => {
          URL.revokeObjectURL(objectUrl);
        }, 1000);
      },
      "image/png",
      1,
    );
  };
  if (isCataloguePayment && checkingOrderValidity) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-white px-4 py-8 sm:px-6">
        <div className="text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-[#1A1349]" />

          <p className="mt-3 text-sm font-semibold text-gray-600">
            Checking your order...
          </p>
        </div>
      </div>
    );
  }

  if (isCataloguePayment && orderLinkExpired) {
    return (
      <div className="flex min-h-[100dvh] flex-col items-center justify-center overflow-y-auto bg-gradient-to-b from-red-50 to-white px-4 py-8 text-center sm:px-6">
        <div className="mb-4 rounded-full border border-red-100 bg-white p-4 shadow-lg sm:p-6">
          <AlertCircle className="h-12 w-12 text-red-500 sm:h-16 sm:w-16" />
        </div>

        <h1 className="mb-2 text-xl font-semibold text-red-600 sm:text-2xl">
          Order Link Expired
        </h1>

        <p className="mb-5 max-w-md text-sm leading-6 text-gray-600 sm:mb-6 sm:text-base">
          This order link has expired. It stays active only for{" "}
          <span className="font-semibold text-gray-800">15 minutes</span>.
        </p>

        <div className="mb-5 flex items-center gap-2 text-xs text-gray-500 sm:mb-6 sm:text-sm">
          <Clock8 size={16} />

          <span>Link validity: 15 minutes</span>
        </div>

        <button
          type="button"
          onClick={() => {
            navigate(
              {
                pathname: "/user",
                search: window.location.search,
              },
              {
                replace: true,
              },
            );
          }}
          className="flex min-h-11 items-center gap-2 rounded-xl bg-green-500 px-5 py-2.5 text-sm font-semibold text-white shadow-md transition hover:bg-green-600 active:scale-[0.99]"
        >
          <UserCircle size={18} />
          View Profile
        </button>
      </div>
    );
  }

  if (!paymentLink) {
    return (
      <div className="mx-auto my-4 w-[calc(100%-24px)] max-w-md rounded-3xl border border-red-200 bg-white p-5 text-center shadow-sm sm:my-8 sm:p-6">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-red-500">
          <AlertIcon />
        </div>

        <h2 className="mt-4 text-xl font-bold text-slate-900">
          Payment link unavailable
        </h2>

        <p className="mt-2 text-sm leading-6 text-slate-500">
          Generate a valid payment link before opening this payment screen.
        </p>
      </div>
    );
  }

  return (
    <section
      className={`
        relative
        isolate
        mx-auto
        flex
        w-full
        max-w-[500px]
        flex-col
        overflow-hidden
        border
        border-white/25
        shadow-[0_20px_60px_rgba(26,19,73,0.28)]

        ${
          embedded
            ? "h-full max-h-full rounded-none sm:rounded-[28px]"
            : "min-h-[100dvh] sm:my-3 sm:h-[calc(100dvh-24px)] sm:min-h-0 sm:rounded-[28px]"
        }
      `}
      style={{
        background: "#1A1349",
      }}
    >
      <div
        className="pointer-events-none absolute -left-24 -top-24 h-56 w-56 rounded-full blur-3xl"
        // style={{
        //     background:
        //         "radial-gradient(circle, rgba(255,255,255,0.26) 0%, rgba(255,255,255,0) 72%)",
        // }}
      />

      <div
        className="pointer-events-none absolute -bottom-24 -right-20 h-64 w-64 rounded-full blur-3xl"
        style={{
          background:
            "radial-gradient(circle, rgba(15,10,45,0.40) 0%, rgba(15,10,45,0) 72%)",
        }}
      />

      <div
        className="
          relative z-10
          min-h-0 flex-1
          overflow-y-auto overscroll-contain
          p-3
          pb-[calc(0.85rem+env(safe-area-inset-bottom))]
          [scrollbar-width:none]
          [-ms-overflow-style:none]
          sm:p-4
          sm:pb-[calc(1rem+env(safe-area-inset-bottom))]
          [&::-webkit-scrollbar]:hidden
        "
      >
        {/* Compact top row */}
        <div className="flex items-center justify-between gap-2 sm:gap-3">
          <div className="flex h-10 min-w-0 items-center px-1 py-1 sm:h-12 sm:min-w-[120px] sm:px-3 sm:py-2">
            <span className="text-lg font-black tracking-wide text-white sm:text-xl">
              CHAAFO
            </span>
          </div>
          <div className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-white/30 bg-white/15 px-2 py-1 text-[8px] font-semibold uppercase tracking-[0.08em] text-white backdrop-blur-md min-[360px]:px-2.5 min-[360px]:text-[9px] sm:gap-2 sm:px-3 sm:py-1.5 sm:text-[10px] sm:tracking-[0.12em]">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
            </span>
            Secure payment
          </div>
        </div>

        {/* Compact amount */}
        <div className="mt-2.5 text-center sm:mt-3">
          <p className="text-[11px] font-medium text-white/75">
            Amount payable
          </p>

          <div className="mt-0.5 flex min-w-0 items-end justify-center gap-1 px-2">
            <span className="pb-0.5 text-lg font-semibold text-white/80">
              ₹
            </span>

            <h1 className="max-w-full truncate text-[32px] font-black tracking-tight text-white min-[360px]:text-4xl sm:text-[42px]">
              {paymentDetails.amount || "0.00"}
            </h1>
          </div>

          <div className="mt-1 text-center">
            <p className="text-[11px] text-white/75">Pay securely to</p>

            <p className="mx-auto mt-0.5 max-w-[92%] truncate text-xs font-bold tracking-wide text-white min-[360px]:text-sm">
              {clientName || paymentDetails.payee || "Merchant"}
            </p>
          </div>
        </div>

        {/* Larger QR with reduced outer padding */}
        <div
          className="mx-auto mt-2.5 max-w-full rounded-[16px] border border-white/35 bg-white shadow-[0_14px_38px_rgba(26,19,73,0.22)] sm:mt-3 sm:rounded-[18px]"
          style={{
            width: "min(86vw, 360px, 42dvh)",
          }}
        >
          <div className="relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-[14px] bg-white sm:rounded-[15px]">
            {!qrGenerated && !qrGenerating && !qrError && (
              <>
                <DummyQr />

                <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80 px-4 text-center backdrop-blur-[3px]">
                  <button
                    type="button"
                    onClick={handleGenerateQr}
                    className="group relative flex h-14 w-14 cursor-pointer items-center justify-center rounded-full bg-[#1A1349] text-white shadow-[0_12px_28px_rgba(26,19,73,0.32)] transition duration-300 hover:scale-105 hover:bg-[#30266D] active:scale-95 sm:h-16 sm:w-16"
                    aria-label="Generate payment QR"
                  >
                    <span className="absolute inset-1 rounded-full border border-white/30" />

                    <RefreshIcon className="h-6 w-6 transition duration-500 group-hover:rotate-180 sm:h-7 sm:w-7" />
                  </button>

                  <p className="mt-2.5 text-xs font-bold text-slate-900 min-[360px]:text-sm sm:mt-3">
                    Tap to generate QR
                  </p>

                  <p className="mt-0.5 px-2 text-[10px] text-slate-500 min-[360px]:text-[11px]">
                    Created securely on this device
                  </p>
                </div>
              </>
            )}

            {qrGenerating && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-white">
                <div className="relative flex h-16 w-16 items-center justify-center sm:h-20 sm:w-20">
                  <div className="absolute inset-0 animate-ping rounded-full border-2 border-violet-200" />

                  <div className="absolute inset-3 animate-spin rounded-full border-4 border-slate-100 border-t-[#1A1349]" />

                  <div className="relative text-[#1A1349]">
                    <QrIcon />
                  </div>
                </div>

                <p className="mt-3 text-sm font-bold text-slate-900">
                  Generating QR
                </p>

                <p className="mt-0.5 text-[11px] text-slate-500">
                  No external QR service is used
                </p>
              </div>
            )}

            {qrGenerated && !qrError && (
              <div className="absolute inset-0 flex items-center justify-center bg-white">
                <div
                  ref={qrContainerRef}
                  className="flex h-full w-full items-center justify-center overflow-hidden bg-white"
                >
                  <canvas
                    ref={canvasRef}
                    className="block shrink-0"
                    aria-label="UPI payment QR code"
                  />
                </div>
              </div>
            )}

            {qrError && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-white p-5 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-500">
                  <AlertIcon />
                </div>

                <p className="mt-3 text-sm font-bold text-slate-900">
                  QR generation failed
                </p>

                <p className="mt-1 text-[11px] text-slate-500">{qrError}</p>

                <button
                  type="button"
                  onClick={handleGenerateQr}
                  className="mt-3 rounded-xl bg-[#1A1349] px-4 py-2 text-xs font-bold text-white hover:bg-[#30266D]"
                >
                  Try again
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="mt-2 flex items-center justify-between gap-2 px-0.5">
          <p className="min-w-0 pr-1 text-[10px] leading-4 text-white/75 min-[360px]:text-[11px] sm:text-[12px]">
            Scan using any UPI-supported app
          </p>

          {qrGenerated && !qrError && (
            <div className="flex shrink-0 items-center gap-1.5">
              <button
                type="button"
                onClick={handleDownloadQr}
                className="inline-flex items-center justify-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[10px] font-semibold text-white backdrop-blur-sm transition hover:text-[#1A1349]"
                aria-label="Download payment QR"
              >
                <DownloadIcon className="h-3.5 w-3.5" />
              </button>

              <button
                type="button"
                onClick={handleRegenerateQr}
                className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-white/30 bg-white/15 px-2.5 py-1.5 text-[10px] font-semibold text-white backdrop-blur-sm transition hover:bg-white hover:text-[#1A1349]"
                aria-label="Refresh payment QR"
              >
                <RefreshIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>

        {/* Pay with Credit */}
        {isCataloguePayment && (checkingCredit || creditAllowed) && (
          <div className="mt-3">
            {checkingCredit ? (
              <div className="flex min-h-[48px] w-full items-center justify-center gap-2 rounded-[14px] border border-white/20 bg-white/10 px-3 backdrop-blur-md sm:min-h-[50px] sm:rounded-[16px] sm:px-4">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />

                <span className="text-center text-[11px] font-semibold text-white/80 sm:text-xs">
                  Checking credit availability...
                </span>
              </div>
            ) : (
              <button
                type="button"
                onClick={handlePayWithCredit}
                disabled={payingWithCredit}
                className="
            group
            flex min-h-[52px] w-full
            items-center justify-between gap-2
            rounded-[14px]
            border border-white/20
            bg-white
            px-3
            sm:min-h-[54px]
            sm:rounded-[16px]
            sm:px-4
            text-left
            shadow-[0_12px_30px_rgba(10,6,40,0.18)]
            transition
            hover:-translate-y-0.5
            hover:shadow-[0_16px_36px_rgba(10,6,40,0.24)]
            disabled:cursor-not-allowed
            disabled:opacity-70
          "
              >
                <div className="flex min-w-0 items-center gap-2.5 sm:gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#F1EFFF] text-[#1A1349] sm:h-10 sm:w-10">
                    {payingWithCredit ? (
                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#1A1349]/20 border-t-[#1A1349]" />
                    ) : (
                      <CreditIcon />
                    )}
                  </div>

                  <div className="min-w-0">
                    <p className="truncate text-xs font-black text-slate-900 sm:text-sm">
                      {payingWithCredit
                        ? "Processing credit"
                        : "Pay with Credit"}
                    </p>

                    <p className="mt-0.5 line-clamp-1 text-[9px] font-medium text-slate-500 min-[360px]:text-[10px]">
                      {payingWithCredit
                        ? "Confirming your payment..."
                        : "Use your available account credit"}
                    </p>
                  </div>
                </div>

                {!payingWithCredit && (
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#1A1349] text-white transition group-hover:translate-x-0.5">
                    <ArrowIcon />
                  </div>
                )}
              </button>
            )}

            {creditError && (
              <div className="mt-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2">
                <p className="text-[11px] font-semibold text-red-600">
                  {creditError}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Compact UPI app section */}
        <div className="mt-2.5 rounded-[16px] border border-white/20 bg-white p-2.5 backdrop-blur-md sm:mt-3 sm:rounded-[18px] sm:p-3">
          <div className="flex min-w-0 items-center gap-2 sm:gap-2.5">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-white p-1 text-[#1A1349] shadow-sm sm:h-9 sm:w-9">
              <img
                src="https://anlook.com/images/website/catalogue/UPILogo.svg"
                alt=""
              />
            </div>

            <div className="min-w-0">
              <p className="text-[11px] font-bold min-[360px]:text-xs">
                Paying on this phone?
              </p>

              <p className="mt-0.5 text-[9px] leading-4 min-[360px]:text-[10px]">
                Choose an app or open your preferred UPI app.
              </p>
            </div>
          </div>

          {/* App-specific buttons */}
          <div className="mt-2.5 grid grid-cols-3 gap-1.5 min-[360px]:gap-2">
            <button
              type="button"
              onClick={() => handleOpenSpecificUpiApp(upiAppLinks.googlePay)}
              className="flex min-h-[50px] min-w-0 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border border-white/25 bg-white/95 px-1 py-1.5 text-slate-800 shadow-sm transition hover:-translate-y-0.5 hover:bg-white min-[360px]:min-h-[54px] min-[360px]:px-2 min-[360px]:py-2"
              aria-label="Pay with Google Pay"
            >
              <GooglePayIcon />

              <span className="max-w-full truncate text-[8px] font-bold min-[360px]:text-[10px]">
                Google Pay
              </span>
            </button>

            <button
              type="button"
              onClick={() => handleOpenSpecificUpiApp(upiAppLinks.phonePe)}
              className="flex min-h-[50px] min-w-0 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border border-white/25 bg-white/95 px-1 py-1.5 text-slate-800 shadow-sm transition hover:-translate-y-0.5 hover:bg-white min-[360px]:min-h-[54px] min-[360px]:px-2 min-[360px]:py-2"
              aria-label="Pay with PhonePe"
            >
              <PhonePeIcon />

              <span className="max-w-full truncate text-[8px] font-bold min-[360px]:text-[10px]">
                PhonePe
              </span>
            </button>

            <button
              type="button"
              onClick={() => handleOpenSpecificUpiApp(upiAppLinks.paytm)}
              className="flex min-h-[50px] min-w-0 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border border-white/25 bg-white/95 px-1 py-1.5 text-slate-800 shadow-sm transition hover:-translate-y-0.5 hover:bg-white min-[360px]:min-h-[54px] min-[360px]:px-2 min-[360px]:py-2"
              aria-label="Pay with Paytm"
            >
              <PaytmIcon />

              <span className="max-w-full truncate text-[8px] font-bold min-[360px]:text-[10px]">
                Paytm
              </span>
            </button>
          </div>

          {/* Generic UPI chooser */}
          <button
            type="button"
            onClick={handleOpenPaymentApp}
            className="group mt-2 flex min-h-[44px] w-full cursor-pointer items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-xs font-black text-white shadow-[0_10px_24px_rgba(26,19,73,0.22)] transition duration-300 hover:-translate-y-0.5 active:translate-y-0 min-[360px]:gap-2 min-[360px]:px-4 min-[360px]:py-2.5 min-[360px]:text-sm"
            style={{
              backgroundColor: "#1A1349",
              color: "#ffffff",
            }}
            onMouseEnter={(event) => {
              event.currentTarget.style.backgroundColor = "#30266D";
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.backgroundColor = "#1A1349";
            }}
          >
            <PaymentIcon />
            Pay with any UPI app
            <ArrowIcon />
          </button>
        </div>

        <div className="mt-2.5 flex items-center justify-center gap-1.5 px-2 text-center text-[8px] leading-4 text-white/60 min-[360px]:text-[9px]">
          <LockIcon />
          Payment information stays securely on your device
        </div>
        <div className="mt-0.5 flex items-center justify-center gap-1.5 px-2 text-center text-[8px] leading-4 text-white/60 min-[360px]:mt-1 min-[360px]:text-[9px]">
          <span
            className={`h-1.5 w-1.5 rounded-full ${
              socketConnected ? "bg-emerald-200" : "bg-amber-200"
            }`}
          />

          {socketConnected
            ? "Waiting for payment confirmation"
            : "Connecting to payment status"}
        </div>
      </div>
    </section>
  );
};

const DummyQr = () => {
  const cells = Array.from({ length: 441 }, (_, index) => {
    const row = Math.floor(index / 21);
    const column = index % 21;

    const topLeft =
      row < 7 &&
      column < 7 &&
      (row === 0 ||
        row === 6 ||
        column === 0 ||
        column === 6 ||
        (row >= 2 && row <= 4 && column >= 2 && column <= 4));

    const topRight =
      row < 7 &&
      column >= 14 &&
      (row === 0 ||
        row === 6 ||
        column === 14 ||
        column === 20 ||
        (row >= 2 && row <= 4 && column >= 16 && column <= 18));

    const bottomLeft =
      row >= 14 &&
      column < 7 &&
      (row === 14 ||
        row === 20 ||
        column === 0 ||
        column === 6 ||
        (row >= 16 && row <= 18 && column >= 2 && column <= 4));

    return (
      topLeft ||
      topRight ||
      bottomLeft ||
      (row * 7 + column * 11 + index) % 5 < 2
    );
  });

  return (
    <div
      className="grid h-full w-full gap-[1px] p-3 opacity-20 min-[360px]:p-4 sm:p-5"
      style={{
        gridTemplateColumns: "repeat(21, minmax(0, 1fr))",
      }}
      aria-hidden="true"
    >
      {cells.map((isActive, index) => (
        <span
          key={index}
          className={isActive ? "rounded-[1px] bg-[#1A1349]" : "bg-transparent"}
        />
      ))}
    </div>
  );
};

const RefreshIcon = ({ className = "h-6 w-6" }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M20 11a8 8 0 0 0-14.9-4" />
    <path d="M4 4v5h5" />
    <path d="M4 13a8 8 0 0 0 14.9 4" />
    <path d="M20 20v-5h-5" />
  </svg>
);

const ShieldIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    className="h-7 w-7"
    aria-hidden="true"
  >
    <path d="M12 3 5 6v5c0 4.7 2.8 8.2 7 10 4.2-1.8 7-5.3 7-10V6l-7-3Z" />
    <path d="m9.5 12 1.7 1.7 3.6-4" />
  </svg>
);

const PaymentIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-5 w-5"
    aria-hidden="true"
  >
    <rect x="2" y="5" width="20" height="14" rx="3" />
    <path d="M2 10h20" />
    <path d="M16 15h2" />
  </svg>
);

const ArrowIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-5 w-5 transition group-hover:translate-x-1"
    aria-hidden="true"
  >
    <path d="M5 12h14" />
    <path d="m13 6 6 6-6 6" />
  </svg>
);

const QrIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    className="h-10 w-10"
    aria-hidden="true"
  >
    <rect x="3" y="3" width="6" height="6" rx="1" />
    <rect x="15" y="3" width="6" height="6" rx="1" />
    <rect x="3" y="15" width="6" height="6" rx="1" />
    <path d="M15 15h2v2h-2zM19 15h2v6h-2M15 19h2v2h-2" />
  </svg>
);

const LockIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className="h-4 w-4"
    aria-hidden="true"
  >
    <rect x="5" y="10" width="14" height="10" rx="2" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
  </svg>
);

const AlertIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className="h-6 w-6"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v6" />
    <path d="M12 17h.01" />
  </svg>
);

const VerifiedIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-5 w-5"
    aria-hidden="true"
  >
    <path d="M12 3 14.1 5.1 17 4.8l.3 2.9L19.5 10 18 12.5l1.5 2.5-2.2 2.3-.3 2.9-2.9-.3L12 22l-2.1-2.1-2.9.3-.3-2.9L4.5 15 6 12.5 4.5 10l2.2-2.3L7 4.8l2.9.3L12 3Z" />
    <path d="m9 12.5 2 2 4-4.5" />
  </svg>
);
const UpiIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-6 w-6"
    aria-hidden="true"
  >
    <path d="M5 5h5l-2 14H3L5 5Z" />
    <path d="M12 5h4l5 7-7 7h-4l7-7-5-7Z" />
  </svg>
);
const DownloadIcon = ({ className = "h-4 w-4" }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M12 3v12" />
    <path d="m7 10 5 5 5-5" />
    <path d="M5 21h14" />
  </svg>
);

const GooglePayIcon = () => (
  <div
    className="flex h-7 w-7 items-center justify-center rounded-full bg-white text-base font-black shadow-sm ring-1 ring-slate-200"
    aria-hidden="true"
  >
    <img
      src="https://anlook.com/images/website/catalogue/GooglePay.svg"
      alt=""
    />
  </div>
);

const PhonePeIcon = () => (
  <div
    className="flex h-7 w-7 items-center justify-center rounded-full bg-[#5F259F] text-xs font-black text-white shadow-sm"
    aria-hidden="true"
  >
    <img src="https://anlook.com/images/website/catalogue/PhonePe.svg" alt="" />
  </div>
);

const PaytmIcon = () => (
  <div
    className="flex h-7 min-w-7 items-center justify-center rounded-lg bg-white px-1 text-[9px] font-black shadow-sm ring-1 ring-slate-200"
    aria-hidden="true"
  >
    <img
      src="https://anlook.com/images/website/catalogue/PaytmLogo.svg"
      alt=""
    />
  </div>
);
const CreditIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-5 w-5"
    aria-hidden="true"
  >
    <rect x="3" y="5" width="18" height="14" rx="3" />

    <path d="M3 10h18" />

    <path d="M7 15h4" />

    <path d="M17 13v4" />

    <path d="M15 15h4" />
  </svg>
);
export default PaymentQr;
